import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import "./styles.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getLeague, upsertParticipant } from "../../../services/api/leagues";

export default function JoinLeagueView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [leagueId, setLeagueId] = React.useState(() => searchParams.get("leagueId") ?? "");
  const [leagueName, setLeagueName] = React.useState("");
  const [leagueLoadError, setLeagueLoadError] = React.useState("");
  const [leagueLoading, setLeagueLoading] = React.useState(false);

  const [email, setEmail] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [subscribedToEmail, setSubscribedToEmail] = React.useState(true);
  const [submitState, setSubmitState] = React.useState({ loading: false, error: "", success: false });

  // If leagueId was pre-populated from URL, load league name
  React.useEffect(() => {
    const id = leagueId.trim();
    if (!id) return;
    setLeagueLoading(true);
    setLeagueLoadError("");
    getLeague(id)
      .then((league) => setLeagueName(league?.name ?? ""))
      .catch(() => setLeagueLoadError("League not found. Double-check the ID."))
      .finally(() => setLeagueLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLeagueIdBlur = () => {
    const id = leagueId.trim();
    if (!id) {
      setLeagueName("");
      setLeagueLoadError("");
      return;
    }
    setLeagueLoading(true);
    setLeagueLoadError("");
    setLeagueName("");
    getLeague(id)
      .then((league) => setLeagueName(league?.name ?? ""))
      .catch(() => setLeagueLoadError("League not found. Double-check the ID."))
      .finally(() => setLeagueLoading(false));
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setSubmitState({ loading: true, error: "", success: false });
    try {
      await upsertParticipant(leagueId.trim(), {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      setSubmitState({ loading: false, error: "", success: true });
    } catch (error) {
      setSubmitState({
        loading: false,
        error: error instanceof Error ? error.message : "Unable to join. Please try again.",
        success: false,
      });
    }
  };

  if (submitState.success) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Stack spacing={3} alignItems="center" sx={{ textAlign: "center" }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64 }} />
          <Typography variant="h4" component="h3">
            You&apos;re in!
          </Typography>
          <Typography color="text.secondary">
            Welcome{leagueName ? ` to ${leagueName}` : ""}. You&apos;ve been added as a
            participant.{subscribedToEmail ? " You'll receive game reminder emails." : ""}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={() => navigate("/search")}>
              Find Games
            </Button>
            <Button variant="outlined" onClick={() => navigate("/")}>
              Home
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" className="join-page">
      <Stack spacing={2}>
        <Typography variant="h3" component="h3">
          Join a League
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Enter your info to register as a participant. Your identity is tied to your email across all leagues.
        </Typography>
      </Stack>

      <Card variant="outlined" className="join-card">
        <CardContent>
          <Box component="form" onSubmit={handleJoin}>
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <TextField
                  label="League ID"
                  required
                  value={leagueId}
                  onChange={(e) => {
                    setLeagueId(e.target.value);
                    setLeagueName("");
                    setLeagueLoadError("");
                  }}
                  onBlur={handleLeagueIdBlur}
                  helperText="Ask your organizer for the league ID."
                  error={!!leagueLoadError}
                />
                {leagueLoading && <LinearProgress sx={{ mt: 0.5 }} />}
                {leagueName && !leagueLoadError && (
                  <Typography variant="caption" color="success.main">
                    League found: <strong>{leagueName}</strong>
                  </Typography>
                )}
                {leagueLoadError && (
                  <Typography variant="caption" color="error">
                    {leagueLoadError}
                  </Typography>
                )}
              </Stack>
              <TextField
                label="Email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                helperText="Used to identify you across leagues and games."
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="First name"
                  required
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <TextField
                  label="Last name"
                  required
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={subscribedToEmail}
                    onChange={(e) => setSubscribedToEmail(e.target.checked)}
                  />
                }
                label="I agree to receive game reminder and registration emails"
              />
              {submitState.error && (
                <Alert severity="error" onClose={() => setSubmitState((s) => ({ ...s, error: "" }))}>
                  {submitState.error}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={submitState.loading || !!leagueLoadError}
              >
                {submitState.loading ? "Joining…" : "Join League"}
              </Button>
              <Button variant="text" onClick={() => navigate("/")}>
                Back to Home
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

